// Copyright Abridged, Inc. 2023,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {TradeShareRequest} from '../models/trade-share-request.model.js';
import {TelefrensCreateGroupResponse} from '../models/telefrens-create-group-response.model.js';
import {OnramperSessionResponse} from '../models/onramper-session-response.model.js';
import {UserProfileResponse} from '../models/user-profile-response.model.js';
import {TelefrensSetBioRequest} from '../models/telefrens-set-bio-request.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by TelefrensController.
 */
export interface TelefrensService {
  /**
   */
  getBridgingAccessToken(): Promise<unknown>;

  /**
   * @param _requestBody
   */
  telefrensBuyShares(_requestBody: TradeShareRequest): Promise<unknown>;

  /**
   * @returns OK
   */
  createGroup(): Promise<TelefrensCreateGroupResponse>;

  /**
   * @param chainId
   * @returns OK
   */
  getSmartAccountBalance(chainId?: number): Promise<{
    balance?: string;
    formattedBalance?: string;
  }>;

  /**
   * @param subjectUserId
   * @returns OK
   */
  getTelefrensBalance(subjectUserId: string): Promise<string>;

  /**
   * @param amount
   * @param subjectUserId
   * @returns OK
   */
  getTelefrensTokenBuyPrice(
    amount: string | undefined,
    subjectUserId: string,
  ): Promise<string>;

  /**
   * @param amount
   * @param subjectUserId
   * @returns OK
   */
  getTelefrensTokenBuyPriceAfterFee(
    amount: string | undefined,
    subjectUserId: string,
  ): Promise<string>;

  /**
   * @param amount
   * @param subjectUserId
   * @returns OK
   */
  getTelefrensTokenSellPrice(
    amount: string | undefined,
    subjectUserId: string,
  ): Promise<string>;

  /**
   * @param amount
   * @param subjectUserId
   * @returns OK
   */
  getTelefrensTokenSellPriceAfterFee(
    amount: string | undefined,
    subjectUserId: string,
  ): Promise<string>;

  /**
   */
  getMetadata(): Promise<unknown>;

  /**
   * @returns OK
   */
  createOnramperSession(): Promise<OnramperSessionResponse>;

  /**
   * @param subjectUserId
   * @returns OK
   */
  getUserInfo(subjectUserId: string): Promise<UserProfileResponse>;

  /**
   * @param query
   * @param limit
   */
  getUsers(query?: string, limit?: number): Promise<unknown>;

  /**
   * @param _requestBody
   */
  telefrensSellShares(_requestBody: TradeShareRequest): Promise<unknown>;

  /**
   * @param _requestBody
   */
  telefrensSetBio(_requestBody: TelefrensSetBioRequest): Promise<unknown>;

  /**
   * @param subjectUserId
   * @returns OK
   */
  getTelefrensSharesSupply(subjectUserId: string): Promise<string>;

  /**
   * @returns OK
   */
  generateStripeOnrampSession(): Promise<{
    sessionToken?: string;
  }>;

  /**
   * @param userOpHash
   * @param chainId
   */
  getUserOperationReceipt(
    userOpHash: string,
    chainId?: number,
  ): Promise<unknown>;

  /**
   */
  triggerCreateGroup(): Promise<unknown>;

  /**
   */
  triggerInvite(): Promise<unknown>;

  /**
   */
  triggerKeyboardButtons(): Promise<unknown>;
}

export class TelefrensServiceProvider implements Provider<TelefrensService> {
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<TelefrensService> {
    const service = await getService<{
      apis: {TelefrensController: TelefrensService};
    }>(this.dataSource);
    return service.apis['TelefrensController'];
  }
}
